/**
 * KAN-597: what, if anything, to tell the learner after they click the footer's
 * completion button ("Complete Module" / "Complete Course" / "Complete") and the
 * progress reload did NOT land them on the course summary.
 *
 * The KAN-367 safety net used to warn "Module not complete yet" on EVERY such
 * click. That assumed the button only existed on the last item. Since KAN-595 a
 * mid-course module shows "Complete Module", so a learner whose module had
 * completed (sidebar checkmark, next module unlocked) was told it had not.
 *
 * Evaluated AFTER the reload, so `itemComplete` / `allModulesComplete` are the
 * server's answer, not the optimistic sidebar status.
 */
export interface CompletionFeedbackInput {
  /** The item the learner clicked Complete on is complete in the reloaded progress. */
  itemComplete: boolean;
  /** That item is the final navigation item (nothing after it). */
  isLastItem: boolean;
  allModulesComplete: boolean;
  /** The course passing gate (KAN-411) is met or not enforced. */
  passingGateSatisfied: boolean;
}

export type CompletionFeedback =
  | { kind: 'none' }
  | { kind: 'course-not-passed' }
  | { kind: 'course-incomplete' }
  | { kind: 'module-incomplete'; finishingCourse: boolean };

export function decideCompletionFeedback(input: CompletionFeedbackInput): CompletionFeedback {
  if (input.allModulesComplete && !input.passingGateSatisfied) {
    return { kind: 'course-not-passed' };
  }
  if (!input.itemComplete) {
    return { kind: 'module-incomplete', finishingCourse: input.isLastItem };
  }
  // The module completed. Mid-course that is the whole job - the footer now
  // offers Next. On the last item it means some OTHER module is still open.
  if (input.isLastItem && !input.allModulesComplete) {
    return { kind: 'course-incomplete' };
  }
  return { kind: 'none' };
}
